import { useCallback, useEffect, useState } from 'react'
import { useApolloClient } from '@apollo/client/react'
import { useExperiment } from '@fs/zion-experiments'
import { i18n } from '@fs/zion-locale'
import { getEncodedArk, getSearchedImageName, getSearchedImageNames } from 'helpers'
import { getImageApidByName, getImageGroupId } from 'components/FormInputs/queries'
import useGroupSearch from './useGroupSearch'

export default function useImageDetailsUrl(apidData) {
  const { groupSearchQueryParams } = useGroupSearch()
  const { imageApid, imageArk, imageGroupNumbers } = groupSearchQueryParams
  const client = useApolloClient()
  const searchedImageName = getSearchedImageName(imageGroupNumbers)
  const imageNamesToLookUp = imageApid || imageArk ? '' : getSearchedImageNames(imageGroupNumbers).join(',')
  const [searchedImages, setSearchedImages] = useState([])

  // Look up every searched image in parallel, since a useLazyQuery would abort all but the last
  useEffect(() => {
    let isCurrent = true
    setSearchedImages([])
    if (!imageNamesToLookUp) return undefined

    const imageNames = imageNamesToLookUp.split(',')
    Promise.all(
      imageNames.map(async (imageName) => {
        try {
          const { data } = await client.query({ query: getImageApidByName, variables: { imageName } })
          const apid = data?.imageApidByName
          // A lone image name search finds just its group, so only several need the group that holds each image
          if (!apid || imageNames.length === 1) return { apid }
          const { data: groupData } = await client.query({ query: getImageGroupId, variables: { imageApid: apid } })
          return { apid, groupId: groupData?.getArtifactParent }
        } catch (err) {
          console.error(`error fetching apid for image name ${imageName}`, err)
          return {}
        }
      })
    ).then((images) => {
      if (isCurrent) setSearchedImages(images)
    })
    return () => {
      isCurrent = false
    }
  }, [client, imageNamesToLookUp])
  const [imageDetailsUrlEx] = useExperiment('imageDetailsUrlEx')

  const getReactImageDetailsUrl = useCallback(
    (group) => {
      const id = group?.id
      const imageApids = group?.imageApids || []
      const imageApidsToUse = [...imageApids]
      // Among several image names, each group opens to the lowest numbered one it holds
      const searchedImage = searchedImageName ? searchedImages[0] : searchedImages.find(({ groupId }) => groupId === id)
      // Insert the searched for image apid into the start of the array
      const apid = imageApid ?? apidData?.imageApid ?? searchedImage?.apid
      if (apid) {
        imageApidsToUse.unshift(apid)
      }
      const encodedArk = imageArk || getEncodedArk({ id, imageApids: imageApidsToUse })
      return `${window.location.origin}/ark:/61903/${encodedArk}?view=explore&groupId=${id}&grid=on`
    },
    [apidData, imageApid, imageArk, searchedImageName, searchedImages]
  )

  const getImageDetailsUrl = useCallback(
    (group) => {
      const searchParams = new URLSearchParams(groupSearchQueryParams)
      const groupUrl =
        imageDetailsUrlEx || i18n.language !== 'zh'
          ? getReactImageDetailsUrl(group)
          : `${window.location.origin}/records/images/image-details?rmsId=${group.id}&${searchParams.toString()}`
      return groupUrl
    },
    [getReactImageDetailsUrl, groupSearchQueryParams, imageDetailsUrlEx]
  )

  return getImageDetailsUrl
}
